import React, { useState } from "react";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { createTheme } from "@mui/material/styles";

import AdminLayout from "../../components/backend/layout/AdminLayout";
import Dashboard from "../../components/backend/Dashboard";

interface AdminPageProps {
  children?: React.ReactNode;
}

// Admin theme configuration
const theme = createTheme({
  palette: {
    primary: {
      main: "#6366f1", // Clean indigo
      light: "#818cf8",
      dark: "#4f46e5",
    },
    secondary: {
      main: "#ec4899", // Clean pink
      light: "#f472b6",
      dark: "#db2777",
    },
    background: {
      default: "#ffffff",
      paper: "#ffffff",
    },
    text: {
      primary: "#111827",
      secondary: "#6b7280",
    },
    success: {
      main: "#10b981",
    },
    warning: {
      main: "#f59e0b",
    },
    error: {
      main: "#ef4444",
    },
    info: {
      main: "#3b82f6",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: "2.5rem",
    },
    h2: {
      fontWeight: 600,
      fontSize: "2rem",
    },
    h3: {
      fontWeight: 600,
      fontSize: "1.5rem",
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.25rem",
    },
    h5: {
      fontWeight: 600,
      fontSize: "1.125rem",
    },
    h6: {
      fontWeight: 600,
      fontSize: "1rem",
    },
  },
  shape: {
    borderRadius: 0,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          boxShadow:
            "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
          border: "1px solid #e5e7eb",
          background: "#ffffff",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 0,
          fontWeight: 600,
          padding: "8px 16px",
          fontSize: "14px",
        },
        contained: {
          background: "#6366f1",
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
          "&:hover": {
            background: "#4f46e5",
            boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.1)",
          },
        },
        outlined: {
          borderColor: "#e5e7eb",
          color: "#6b7280",
          "&:hover": {
            background: "#f9fafb",
            borderColor: "#6366f1",
            color: "#6366f1",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          fontWeight: 600,
          fontSize: "12px",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: "#ffffff",
          borderRight: "1px solid #e5e7eb",
          borderRadius: 0,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: "#ffffff",
          borderBottom: "1px solid #e5e7eb",
          borderRadius: 0,
        },
      },
    },
  },
});

const AdminPage: React.FC<AdminPageProps> = ({ children }) => {
  const [open, setOpen] = useState(true);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AdminLayout open={open} onDrawerToggle={handleDrawerToggle}>
        {children || <Dashboard />}
      </AdminLayout>
    </ThemeProvider>
  );
};

export default AdminPage;
